/**
 * Pure decision for the instrument live-audition invalidation pass.
 * Kept free of React/Web Audio so the transport-start guard is unit-testable.
 *
 * Editing the piano roll during playback invalidates notes already queued in the
 * Web Audio lookahead window, so they must be cancelled and rebuilt. Pressing
 * Play must NOT cancel: the note scheduler effect runs earlier in the same commit
 * and has just queued the notes at the start beat. Cancelling them there kills
 * voices that have not sounded yet (noteOn registers a voice synchronously even
 * when its scheduledTime is in the future), and the scheduler's window filter
 * never retries a beat the transport has already passed — so the first notes of
 * a progression are lost for the whole pass.
 */
export function nextAuditionState(prevClips, nextClips, isPlaying, options = {}) {
  // Stopped: forget the baseline so the next Play is treated as a fresh start.
  if (!isPlaying) return { clips: null, cancel: false };
  // Transport-start run: adopt the baseline, never cancel.
  if (prevClips === null || prevClips === undefined) return { clips: nextClips, cancel: false };
  // A note captured by live recording lands at a beat the transport has already
  // passed, so it cannot change anything inside the lookahead window. Tearing
  // the schedule down for it made every recorded note stutter the drums —
  // recording was fighting playback. Adopt the new clips without cancelling.
  if (nextClips !== undefined && nextClips === options.recordingWriteClips) {
    return { clips: nextClips, cancel: false };
  }
  // Unrelated re-render: same clip identity, nothing queued has gone stale.
  if (prevClips === nextClips) return { clips: prevClips, cancel: false };
  // A real edit during playback: drop the stale queue and let the scheduler rebuild.
  return { clips: nextClips, cancel: true };
}

// Sources closer than this are already inside the browser's render/output
// pipeline. Letting them finish avoids clicks; edits are applied immediately
// after this tiny protected window.
export const LIVE_EDIT_GUARD_SECONDS = 0.012;

/** Split queued Web Audio work without touching anything already audible. */
export function partitionLiveEditSchedule(entries = [], now = 0, guardSeconds = LIVE_EDIT_GUARD_SECONDS) {
  const cutoff = Math.max(0, Number(now) || 0) + Math.max(0, Number(guardSeconds) || 0);
  return (Array.isArray(entries) ? entries : []).reduce((result, entry) => {
    const startTime = Number(entry?.startTime);
    if (Number.isFinite(startTime) && startTime > cutoff) result.cancel.push(entry);
    else result.keep.push(entry);
    return result;
  }, { cutoff, keep: [], cancel: [] });
}
